import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LinkBox } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import {
  DialogAdditionalParamKey,
  DialogKey,
} from '@sorare/core/src/components/navigation/WithDialogs';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { DecisivePlayerPickerTaskClaimStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskClaimStory/index.tsx';
import { DecisivePickerSlot } from '#src/components/missions/picker/DecisivePickerSlot/index.tsx';

import type { DecisivePlayerPickerTaskRow_decisivePlayerPickerTask } from './__generated__/index.graphql.ts';

const Wrapper = styled(LinkBox).attrs({ as: 'button' })`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-200);
  text-align: left;
  position: relative;
  cursor: pointer;
  &:disabled {
    cursor: default;
  }
`;

const Chevron = styled.div`
  color: var(--c-nd-600);
`;

const Daily = styled(LabelS)`
  color: var(--c-arena-primary);
  letter-spacing: 0.04em;
`;

const SlotsRow = styled(Horizontal).attrs({ gap: 1 })`
  flex: 1;
  align-items: center;
  min-width: 0;
`;

type Props = {
  task: DecisivePlayerPickerTaskRow_decisivePlayerPickerTask;
};

export const DecisivePlayerPickerTaskRow = ({ task }: Props) => {
  const {
    id,
    aasmState,
    taskAppearances,
    title,
    description,
    rewardConfigs,
    maxAppearancesCount,
    mode,
  } = task;
  const taskAppearance = taskAppearances.at(0);
  const openDialog = useOpenDialog();
  const [showClaimTaskStory, setShowClaimTaskStory] = useState(false);

  const rewardConfig = rewardConfigs.find(
    isTypeFn('InGameCurrencyRewardConfig')
  );

  const isCompleted = aasmState === ManagerProgressionTaskState.COMPLETED;
  const isClaimed = aasmState === ManagerProgressionTaskState.CLAIMED;
  const isReady = aasmState === ManagerProgressionTaskState.READY;

  const slotCount = maxAppearancesCount ?? 3;

  const onClick = () => {
    if (isCompleted) {
      setShowClaimTaskStory(true);
      return;
    }
    if (isClaimed && taskAppearance?.game?.id && taskAppearance?.player?.slug) {
      openDialog(DialogKey.game, taskAppearance.game.id, {
        additionalParams: {
          [DialogAdditionalParamKey.playerSlugs]: taskAppearance.player.slug,
        },
      });
      return;
    }
    if (isReady) {
      if (!taskAppearance || !taskAppearance.locked) {
        openDialog(DialogKey.decisivePlayerPicker, id);
        return;
      }
      if (taskAppearance.game?.id) {
        openDialog(DialogKey.game, taskAppearance.game.id, {
          additionalParams: {
            [DialogAdditionalParamKey.playerSlugs]: taskAppearance.player.slug,
          },
        });
      }
    }
  };

  return (
    <>
      <Wrapper onClick={onClick}>
        <Vertical gap={1.5}>
          <SBHorizontal gap={2}>
            <Horizontal gap={2}>
              <Vertical gap={0.5}>
                <Daily bold uppercase>
                  <FormattedMessage defaultMessage="Daily" />
                </Daily>
                <LabelL bold>{title}</LabelL>
                <BodyM color="var(--c-nd-600)">{description}</BodyM>
              </Vertical>
            </Horizontal>
            <Vertical gap={1} center>
              <Chevron>
                <FontAwesomeIcon icon={iconChevronRight} />
              </Chevron>
              {!!rewardConfig && (
                <AnyRewardConfig
                  anyRewardConfig={rewardConfig}
                  variant={Variant.INLINE_CHIP}
                />
              )}
            </Vertical>
          </SBHorizontal>
          <SlotsRow>
            {Array.from({ length: slotCount }, (_, index) => {
              const appearance = taskAppearances.find(a => a.index === index);
              return (
                <DecisivePickerSlot
                  // eslint-disable-next-line react/no-array-index-key
                  key={`slot-${index}`}
                  index={index}
                  taskAppearance={appearance}
                  card={appearance?.card}
                  mode={mode}
                />
              );
            })}
          </SlotsRow>
        </Vertical>
      </Wrapper>
      {!!showClaimTaskStory && (
        <DecisivePlayerPickerTaskClaimStory
          task={task}
          onClose={() => setShowClaimTaskStory(false)}
        />
      )}
    </>
  );
};

DecisivePlayerPickerTaskRow.fragments = {
  decisivePlayerPickerTask: gql`
    fragment DecisivePlayerPickerTaskRow_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      title
      description
      templateIconUrl
      aasmState
      maxAppearancesCount
      mode
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      taskAppearances {
        id
        index
        locked
        status
        player {
          slug
          squaredPictureUrl
          displayName
        }
        game {
          id
          date
        }
        card {
          slug
          ...DecisivePickerSlot_card
        }
        ...DecisivePickerSlot_taskAppearance
      }
      ...DecisivePlayerPickerTaskClaimStory_decisivePlayerPickerTask
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${DecisivePlayerPickerTaskClaimStory.fragments.taskInterface}
    ${DecisivePickerSlot.fragments.taskAppearance}
    ${DecisivePickerSlot.fragments.card}
  ` as TypedDocumentNode<DecisivePlayerPickerTaskRow_decisivePlayerPickerTask>,
};
